import { CheckIcon, FlagIcon, FlagOffIcon, XIcon } from "lucide-react";

import { QA } from "@/client";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/libs/react-query";
import { cn } from "@/utils";

import { qaQueryKey } from "../api/fetch-qa";
import { useUpdateQa } from "../api/update-qa";

type QaItemProps = {
  qa: QA;
};

export function QaItem(props: QaItemProps) {
  const { qa } = props;
  const { mutateAsync: updateQa, isPending } = useUpdateQa({
    onSuccess: (_, data) => {
      queryClient.setQueryData(
        qaQueryKey({
          congressId: qa.congressId,
          eventType: qa.sectionId ? "SECTION" : "ABSTRACT",
          contentId: qa.sectionId || qa.abstractId,
        }),
        (old: QA[]) => {
          if (!old) return [];
          return old.map((qa) =>
            qa.id === data.id ? { ...qa, ...data.requestBody } : qa,
          );
        },
      );
    },
  });

  const flagQa = async () => {
    await updateQa({
      id: qa.id,
      requestBody: {
        isFlagged: true,
      },
    });
  };

  const unflagQa = async () => {
    await updateQa({
      id: qa.id,
      requestBody: {
        isFlagged: false,
      },
    });
  };

  const answerQa = async () => {
    await updateQa({
      id: qa.id,
      requestBody: {
        isAnswered: true,
      },
    });
  };

  const unAnsweredQa = async () => {
    await updateQa({
      id: qa.id,
      requestBody: {
        isAnswered: false,
      },
    });
  };

  return (
    <div className="flex items-center justify-between border-b border-border py-2">
      <p className={cn(qa.isAnswered && "text-muted line-through")}>
        {qa.question}
      </p>

      <div className="ml-auto flex items-center gap-2">
        {qa.isAnswered ? (
          <Button
            variant="outline"
            size="icon"
            disabled={isPending}
            onClick={unAnsweredQa}
          >
            <XIcon className="size-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="icon"
            disabled={isPending}
            onClick={answerQa}
          >
            <CheckIcon className="size-4" />
          </Button>
        )}

        {qa.isFlagged ? (
          <Button
            variant="outline"
            size="icon"
            onClick={unflagQa}
            disabled={isPending}
          >
            <FlagOffIcon className="size-4" />
          </Button>
        ) : (
          <Button
            variant="outline"
            size="icon"
            onClick={flagQa}
            disabled={isPending}
          >
            <FlagIcon className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
